import { useState } from 'react';
import { IconTile } from './Icons';
import { getPolicy } from '../lib/policy';
import { hasFeature } from '../lib/plans';

// Subir este número cuando la guía cambie mucho: la vuelven a ver todos una vez.
export const GUIDE_VERSION = 1;

const Tip = ({ children }) => (
  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 leading-relaxed"><b>Tené en cuenta:</b> {children}</p>
);
const Li = ({ children }) => (
  <li className="flex gap-2 text-sm text-gray-600 leading-relaxed"><span className="text-sky-600 mt-0.5">•</span><span>{children}</span></li>
);

// Guía de uso para el empleado: se arma según lo que tiene activo su organización
export function buildGuideSteps({ cfg, features, orgName, profile }) {
  const pol = getPolicy();
  const m = cfg?.methods || { phone: true };
  const tol = pol.lateness.tolerance_minutes;
  const jp = Number(profile?.justified_deduct_pct ?? pol.payroll.justified_deduct_pct ?? 0);
  const steps = [];

  steps.push({
    icon: 'dashboard', title: `Bienvenido/a a ${orgName}`,
    body: (
      <div className="space-y-3">
        <p className="text-sm text-gray-600 leading-relaxed">Esta app es tu lugar para registrar tu entrada y salida, ver tus horarios, pedir vacaciones o licencias, justificar una falta y consultar tu sueldo.</p>
        <p className="text-sm text-gray-600 leading-relaxed">Te mostramos lo esencial en un minuto. Si más adelante te queda una duda, podés volver a ver esta guía cuando quieras con el botón <b>?</b> de arriba.</p>
      </div>
    ),
  });

  steps.push({
    icon: m.phone ? 'phone' : m.qr_screen ? 'qr' : 'keypad', title: 'Cómo marcar tu entrada y salida',
    body: (
      <div className="space-y-3">
        <ul className="space-y-2">
          {m.phone && <Li><b>Desde tu celular:</b> tocá «Marcar entrada» al llegar y «Marcar salida» al irte.{cfg?.phone?.require_location !== false && ' Te va a pedir tu ubicación: tenés que estar en el lugar de trabajo.'}{cfg?.phone?.require_biometric !== false && ' Después confirmás con tu huella o Face ID.'}</Li>}
          {m.qr_screen && <Li><b>Con el QR:</b> en el local hay una pantalla con un código que cambia solo. Tocá «Escanear QR» y apuntale con la cámara.</Li>}
          {m.kiosk && <Li><b>En el kiosco:</b> ingresá tu código personal de 6 dígitos en la tablet de la entrada. No se lo digas a nadie.</Li>}
          {cfg?.network?.restrict && <Li>Solo se puede marcar <b>conectado a la red del local</b>.</Li>}
        </ul>
        <Tip>cada marca queda guardada con día y hora. Marcá siempre desde tu celular habitual: si alguien marca por vos o se usa otro equipo, te avisamos a vos y a tu empleador. Si te olvidás de marcar, avisale a tu administrador.</Tip>
      </div>
    ),
  });

  steps.push({
    icon: 'records', title: 'Tardanzas y jornada',
    body: (
      <div className="space-y-3">
        <ul className="space-y-2">
          <Li>Tenés <b>{tol} minutos de tolerancia</b> para llegar.{pol.lateness.mode === 'subtract' ? ' Si te pasás, se cuenta solo lo que excede la tolerancia.' : ' Si te pasás, se cuentan todos los minutos de la tardanza.'}</Li>
          <Li>Tu jornada es de <b>{pol.hours.daily_hours} horas</b>. En «Historial» ves cada día con horas trabajadas, extras y faltantes.</Li>
          {pol.hours.recover_late_time && <Li>Si llegás tarde, tenés que <b>salir más tarde</b> para completar la jornada.</Li>}
          <Li>Si tu turno cruza la medianoche (por ejemplo de 18 a 02), marcá la salida cuando termine: queda registrada en el día en que empezaste.</Li>
        </ul>
      </div>
    ),
  });

  if (hasFeature(features, 'licencias')) {
    steps.push({
      icon: 'licencias', title: 'Si faltás: justificá con certificado',
      body: (
        <div className="space-y-3">
          <ul className="space-y-2">
            <Li>Entrá a <b>Licencias → «Justificar una falta con certificado»</b>. Elegí el día, contá el motivo y adjuntá el certificado (PDF o foto, hasta 5 MB).</Li>
            <Li>Tu administrador lo revisa y te avisa si la falta quedó justificada.</Li>
            <Li>{jp <= 0 ? 'Una falta justificada no se descuenta de tu sueldo.' : `Una falta justificada se descuenta al ${jp}% según las reglas de tu organización.`}</Li>
            {pol.payroll.justification_requires_certificate && <Li>En tu organización el certificado es <b>obligatorio</b> para justificar.</Li>}
          </ul>
          <Tip>podés justificar faltas de los últimos 60 días. Los certificados pueden tener datos de salud: solo los ve tu administrador.</Tip>
        </div>
      ),
    });
  }

  const vac = hasFeature(features, 'vacaciones');
  const lic = hasFeature(features, 'licencias');
  if (vac || lic) {
    steps.push({
      icon: vac ? 'vacaciones' : 'licencias', title: [vac && 'Vacaciones', lic && 'licencias'].filter(Boolean).join(' y ').replace(/^./, (c) => c.toUpperCase()),
      body: (
        <ul className="space-y-2">
          {vac && <Li>En <b>Vacaciones</b> ves cuántos días te corresponden y pedís las fechas que querés. El pedido queda «pendiente» hasta que tu administrador lo apruebe.</Li>}
          {lic && <Li>En <b>Licencias</b> pedís las especiales (estudio, casamiento, duelo, nacimiento…). Podés adjuntar el comprobante.</Li>}
          <Li>Vas a ver el estado de cada pedido y la respuesta de tu administrador.</Li>
        </ul>
      ),
    });
  }

  const extras = [];
  if (hasFeature(features, 'tareas')) extras.push(<Li key="t">En <b>Tareas</b> aparecen las que te asigna tu administrador, con fecha límite y prioridad.</Li>);
  if (hasFeature(features, 'payroll')) extras.push(<Li key="p">En <b>Sueldo</b> ves tu recibo, los descuentos y las horas extra del mes.</Li>);
  extras.push(<Li key="h">En <b>Horario</b> y <b>Calendario</b> ves tus días de trabajo, feriados y excepciones.</Li>);
  extras.push(<Li key="a">Los avisos importantes (por ejemplo una marca desde otro celular o la respuesta a una justificación) aparecen arriba de la pantalla principal.</Li>);
  steps.push({ icon: 'tareas', title: 'Tu día a día', body: <ul className="space-y-2">{extras}</ul> });

  steps.push({
    icon: 'user', title: 'Tu privacidad',
    body: (
      <div className="space-y-3">
        <ul className="space-y-2">
          <Li>Guardamos el <b>día, la hora y el lugar</b> de cada marca, solo en el momento en que marcás. No seguimos tu ubicación.</Li>
          <Li>Tu <b>huella o rostro nunca sale de tu celular</b>: la app solo recibe una confirmación.</Li>
          <Li>Los certificados que subas los ve únicamente tu administrador.</Li>
        </ul>
        <p className="text-xs text-gray-400">Más detalle en la <a className="text-sky-700 underline" href="/privacidad" target="_blank" rel="noreferrer">Política de Privacidad</a>.</p>
      </div>
    ),
  });

  steps.push({
    icon: 'phone', title: 'Un último consejo',
    body: (
      <div className="space-y-3">
        <p className="text-sm text-gray-600 leading-relaxed">Agregá la app a la pantalla de inicio de tu celular para abrirla como cualquier otra aplicación:</p>
        <ul className="space-y-2">
          <Li><b>iPhone (Safari):</b> botón Compartir → «Agregar a pantalla de inicio».</Li>
          <Li><b>Android (Chrome):</b> menú ⋮ → «Instalar aplicación».</Li>
        </ul>
        <p className="text-sm text-gray-600 leading-relaxed">Ya está. Cuando quieras repasar algo, tocá el botón <b>?</b> de arriba.</p>
      </div>
    ),
  });
  return steps;
}

export default function EmployeeGuide({ open, onClose, cfg, features, orgName, profile }) {
  const [i, setI] = useState(0);
  if (!open) return null;
  const steps = buildGuideSteps({ cfg, features, orgName, profile });
  const s = steps[Math.min(i, steps.length - 1)];
  const last = i >= steps.length - 1;
  const close = () => { setI(0); onClose(); };
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <div role="dialog" aria-modal="true" aria-label="Guía de uso" className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-6 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <IconTile name={s.icon} />
            <div>
              <p className="text-[11px] font-bold text-sky-700 uppercase tracking-wide">Guía · {i + 1} de {steps.length}</p>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">{s.title}</h2>
            </div>
          </div>
          <button onClick={close} aria-label="Cerrar" className="text-gray-400 w-8 h-8 rounded-full hover:bg-gray-100 flex-shrink-0">✕</button>
        </div>
        <div className="px-6 py-3 overflow-y-auto flex-1">{s.body}</div>
        <div className="px-6 pb-6 pt-3 space-y-3">
          <div className="flex justify-center gap-1.5">
            {steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? 'w-5 bg-sky-700' : 'w-1.5 bg-gray-200'}`} />)}
          </div>
          <div className="flex gap-2">
            {i > 0 ? <button onClick={() => setI(i - 1)} className="px-5 py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Atrás</button>
              : <button onClick={close} className="px-5 py-3 rounded-2xl text-sm font-bold text-gray-400">Saltar</button>}
            <button onClick={() => (last ? close() : setI(i + 1))} className="flex-1 py-3 rounded-2xl text-sm font-bold text-white" style={{ background: 'var(--brand-grad)' }}>
              {last ? 'Entendido' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
